import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { reportsApi } from '@/api/reports';
import { ReportStatus, type TriggerReportRequest } from '@/api/types';

/** All reports (of any type) generated for a competition. */
export function useReportsByCompetition(competitionId: string | undefined) {
  return useQuery({
    queryKey: ['reports', 'list', competitionId],
    queryFn: () => reportsApi.listByCompetition(competitionId as string),
    enabled: Boolean(competitionId),
  });
}

/** Polls a single report until it leaves Pending/Processing (async worker-generated PDFs, etc.). */
export function useReport(reportId: string | undefined) {
  return useQuery({
    queryKey: ['reports', 'detail', reportId],
    queryFn: () => reportsApi.getById(reportId as string),
    enabled: Boolean(reportId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === ReportStatus.Pending || status === ReportStatus.Processing ? 2000 : false;
    },
  });
}

export function useTriggerReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: TriggerReportRequest) => reportsApi.trigger(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['reports', 'list', data.competitionId] });
    },
  });
}
